import React from 'react';
import { motion } from 'framer-motion';
import { Check, Minus, Info, ArrowRight } from 'lucide-react';
const TOOLS = [{
  id: 'copilot',
  name: 'GitHub Copilot'
}, {
  id: 'cursor',
  name: 'Cursor'
}, {
  id: 'claude',
  name: 'Claude Code'
}, {
  id: 'windsurf',
  name: 'Windsurf'
}, {
  id: 'augment',
  name: 'Augment Code'
}, {
  id: 'gemini',
  name: 'Gemini Code Assist'
}, {
  id: 'codex',
  name: 'OpenAI Codex'
}];
type CellValue = string | boolean;
interface Feature {
  name: string;
  values: Record<string, CellValue>;
}
const FEATURES: Feature[] = [{
  name: 'Base Price (Enterprise/Business)',
  values: {
    copilot: '$39/user/mo',
    cursor: '$40/user/mo',
    claude: 'Pay-per-token',
    windsurf: '$40/user/mo',
    augment: '$50–$100/user/mo',
    gemini: '$45/user/mo',
    codex: 'Pay-per-token'
  }
}, {
  name: 'Billing Model',
  values: {
    copilot: 'Per Seat',
    cursor: 'Per Seat',
    claude: 'Consumption',
    windsurf: 'Per Seat',
    augment: 'Seat + Credits',
    gemini: 'Per Seat',
    codex: 'Consumption'
  }
}, {
  name: 'Underlying Models',
  values: {
    copilot: 'OpenAI (GPT-4o)',
    cursor: 'Multi (Claude, GPT)',
    claude: 'Claude 3.5 Sonnet',
    windsurf: 'Multi-model',
    augment: 'Claude + GPT',
    gemini: 'Gemini 2.5',
    codex: 'GPT-5 Codex / o-series'
  }
}, {
  name: 'Context Window',
  values: {
    copilot: 'Standard',
    cursor: 'Large (Codebase-wide)',
    claude: '200k tokens',
    windsurf: 'Deep Context',
    augment: '200k+ (Context Engine)',
    gemini: '1M tokens',
    codex: '256k tokens'
  }
}, {
  name: 'Free Tier',
  values: {
    copilot: 'Limited',
    cursor: 'Hobby',
    claude: false,
    windsurf: 'Yes',
    augment: 'Trial',
    gemini: 'Generous (Individuals)',
    codex: 'Via ChatGPT'
  }
}, {
  name: 'Idle Seat Waste Risk',
  values: {
    copilot: 'High',
    cursor: 'High',
    claude: 'None',
    windsurf: 'High',
    augment: 'Medium',
    gemini: 'High',
    codex: 'None'
  }
}, {
  name: 'Token Inefficiency Risk',
  values: {
    copilot: 'None (Flat rate)',
    cursor: 'None (Flat rate)',
    claude: 'High',
    windsurf: 'None (Flat rate)',
    augment: 'Medium (Credits)',
    gemini: 'None (Flat rate)',
    codex: 'High'
  }
}, {
  name: 'Enterprise SSO/SAML',
  values: {
    copilot: true,
    cursor: true,
    claude: true,
    windsurf: true,
    augment: true,
    gemini: true,
    codex: true
  }
}, {
  name: 'SOC2 Compliance',
  values: {
    copilot: true,
    cursor: true,
    claude: true,
    windsurf: 'Pending/Yes',
    augment: true,
    gemini: true,
    codex: true
  }
}];
function renderCell(value: CellValue) {
  if (typeof value === 'boolean') {
    return value ? <Check className="text-emerald-500" size={20} data-id="element-400" /> : <Minus className="text-slate-600" size={20} data-id="element-401" />;
  }
  return value;
}
export function PricingComparison() {
  return <div className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative" data-id="element-402">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-emerald-500/5 blur-[100px] pointer-events-none rounded-full" data-id="element-403"></div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="max-w-3xl mb-16 relative z-10" data-id="element-404">
        <div className="inline-block bg-slate-900 border border-slate-800 text-emerald-400 px-3 py-1 rounded-full text-sm font-bold mb-6 tracking-wide" data-id="element-405">
          LIVE DATA
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-50 mb-6 tracking-tight" data-id="element-406">
          AI Coding Tool Pricing Comparison
        </h1>
        <p className="text-xl text-slate-400 leading-relaxed" data-id="element-407">
          A maintained, objective breakdown of enterprise costs for the leading
          AI coding assistants — Copilot, Cursor, Claude Code, Windsurf,
          Augment, Gemini Code Assist, and OpenAI Codex. Updated for Q3 2026.
        </p>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-slate-900 rounded-xl border border-slate-800 shadow-2xl overflow-hidden mb-16 relative z-10" data-id="element-408">
        <div className="overflow-x-auto" data-id="element-409">
          <table className="w-full text-left border-collapse min-w-[900px]" data-id="element-410">
            <thead data-id="element-411">
              <tr className="bg-slate-950 border-b border-slate-800" data-id="element-412">
                <th className="p-5 font-bold text-slate-300 uppercase tracking-wider text-xs sticky left-0 bg-slate-950 z-10 min-w-[200px]" data-id="element-413">
                  Feature / Cost Driver
                </th>
                {TOOLS.map(tool => <th key={tool.id} className="p-5 font-bold text-slate-300 uppercase tracking-wider text-xs whitespace-nowrap" data-id="element-414">
                    {tool.name}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50" data-id="element-415">
              {FEATURES.map((row, idx) => <motion.tr initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.05
            }} key={row.name} className="hover:bg-slate-800/50 transition-colors group" data-id="element-416">
                  <td className="p-5 text-sm font-medium text-slate-300 group-hover:text-slate-50 transition-colors sticky left-0 bg-slate-900 group-hover:bg-slate-800/50 z-10" data-id="element-417">
                    {row.name}
                  </td>
                  {TOOLS.map(tool => <td key={tool.id} className="p-5 text-sm text-slate-400 font-mono group-hover:text-emerald-400 transition-colors whitespace-nowrap" data-id="element-418">
                      {renderCell(row.values[tool.id])}
                    </td>)}
                </motion.tr>)}
            </tbody>
          </table>
        </div>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8 relative z-10" data-id="element-419">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-slate-900 border border-slate-800 p-8 rounded-xl" data-id="element-420">
          <h3 className="text-xl font-bold text-slate-50 mb-4" data-id="element-421">
            The Seat vs. Consumption Dilemma
          </h3>
          <p className="text-slate-400 leading-relaxed mb-8" data-id="element-422">
            Flat-rate seat models (Copilot, Cursor, Gemini) offer predictable
            billing but suffer from "idle seat waste"—where you pay $40/mo for
            developers who rarely use the tool. Consumption models (Claude Code,
            Codex) and credit models (Augment) eliminate idle waste but
            introduce unpredictable spikes during heavy refactoring sessions.
          </p>
          <a href="https://waydev.co/pricing" className="inline-flex items-center gap-2 text-emerald-400 font-bold hover:text-emerald-300 transition-colors glow-text-emerald" data-id="element-423">
            Track actual usage with TokenMeter <ArrowRight size={16} data-id="element-424" />
          </a>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="bg-emerald-500/10 border border-emerald-500/20 p-8 rounded-xl relative overflow-hidden" data-id="element-425">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/20 blur-3xl rounded-full pointer-events-none" data-id="element-426"></div>
          <h3 className="text-xl font-bold text-emerald-400 mb-6 flex items-center gap-2" data-id="element-427">
            <Info size={24} data-id="element-428" /> Hidden Costs to Watch
          </h3>
          <ul className="space-y-6 text-slate-300" data-id="element-429">
            <li className="flex gap-4" data-id="element-430">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0 glow-box-emerald" data-id="element-431"></div>
              <span data-id="element-432">
                <strong className="text-slate-50" data-id="element-433">Context Window Bloat:</strong>{' '}
                In consumption models, sending entire codebases repeatedly can
                cost $5+ per query.
              </span>
            </li>
            <li className="flex gap-4" data-id="element-434">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0 glow-box-emerald" data-id="element-435"></div>
              <span data-id="element-436">
                <strong className="text-slate-50" data-id="element-437">Shadow IT:</strong> Developers
                expensing personal $20/mo Pro tiers instead of using corporate
                licenses.
              </span>
            </li>
          </ul>
        </motion.div>
      </div>
    </div>;
}